(ns manatee.qa.view.answer
  (:require [manatee.admin.task-config.subs :as task-config-subs]
            [manatee.annotation.components :as annotation-components]
            [manatee.annotation.handlers :as annotation-handlers]
            [manatee.annotation.ids :as annotation-ids]
            [manatee.annotation.subs :as annotation-subs]
            [manatee.components.item :as annotation-item]
            [manatee.components.modal.handlers :as modal]
            [manatee.domain.task :as task-config]
            [manatee.models.differences :as differences]
            [manatee.qa.handlers :as handlers]
            [manatee.qa.subs :as subs]
            [manatee.qa.view.shared :as view-shared]
            [manatee.spec.core :as spec]
            [manatee.ui.templates :as templates]
            [manatee.user.components :as user-components]
            [manatee.user.subs :as user-subs]
            [medley.core :as medley]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [rothko.button.component :refer [button]]
            [rothko.form2.checkbox.component :as checkbox]
            [rothko.form2.component :as form]
            [rothko.form2.input.component :as input]
            [rothko.form2.validation :as validation]
            [rothko.icon-animated.component :refer [spinner]]
            [rothko.layout.component :refer [layout]]
            [rothko.panel.component :refer [panel]]))

(defn report-details
  [{:keys [notes reporter]} {:keys [comment]}]
  [panel {:appearance #{:simple}}
   [:div
    [:h5  "Report Details"]
    [:ul
     [:li [:span [:strong "Reporter: "] [user-components/full-name reporter]]]
     [:li [:span [:strong "Comment: "] comment]]
     [:li [:span [:strong "Note: "] notes]]]]])

(defn annotate-item
  [item edit props]
  (let [item (-> item
                 (medley/assoc-some :feedback (:feedback edit))
                 (assoc :edit edit
                        :qa-edit-id (:id edit)))]
    [annotation-item/annotate-item item (merge props
                                               {:autosaving-disabled? true
                                                :spellcheck-disabled? true})]))

(defn loading-spinner
  []
  [:div.qa-loading [spinner {:appearance #{:primary}
                             :size 40}]])

(defn give-feedback-button
  [set-show-feedback-drawer!]
  [:div
   [button {:appearance #{:primary}
            :on-click #(set-show-feedback-drawer! true)}
    "Give feedback"]])

(defn feedback-form
  [set-show-feedback-drawer! item edit]
  (let [task-config @(rf/subscribe [::task-config-subs/task-config (:task-config-id item)])
        {:keys [field-name]} (task-config/non-scoring-field task-config)]
    [panel {:appearance #{:primary}}
     [:h4 "Feedback"]
     [form/form
      {::form/on-submit #(do (rf/dispatch [::handlers/submit-feedback {:edit (cond-> edit
                                                                               field-name (update field-name differences/remove-flags)
                                                                               (:remove? %) (dissoc field-name))
                                                                       :comment (:comment %)}])
                             (set-show-feedback-drawer! false))}

      {:comment
       {:appearance #{:primary}
        ::form/label "Comment"
        ::input/type :textarea
        ::validation/messages {::spec/has-content "Comment too short"}
        ::validation/spec ::spec/has-content}
       :remove?
       {::form/name :remove?
        ::form/initial-value false
        ::checkbox/option true
        ::checkbox/label (str "Remove " (some-> field-name name))}}
      (fn [{:keys [::form/submit!]}
           {:keys [comment remove?]}]
        [:form.qa-feedback-form
         [input/field comment]
         (when field-name
           [checkbox/field remove?])
         [:div.qa-feedback-form__actions
          [button
           {:appearance #{:primary}
            :on-click submit!}
           "Submit Feedback"]
          [button
           {:appearance #{:secondary}
            :on-click #(set-show-feedback-drawer! false)}
           "Cancel"]]])]]))

(defn send-feedback
  [_item _edit]
  (let [show-feedback-drawer? (r/atom false)
        set-show-feedback-drawer! #(reset! show-feedback-drawer? %)]
    (fn [item edit]
      (if @show-feedback-drawer?
        [feedback-form set-show-feedback-drawer! item edit]
        [give-feedback-button set-show-feedback-drawer!]))))

(defn approve-button
  [item edit]
  (when (or (#{:requires-approval :rejected-requires-approval} (:status edit)) (:evaluator-edit? edit))
    [button {:appearance #{:primary :full-width}
             :on-click #(rf/dispatch [::handlers/approve-edit item edit])}
     (str "Approve")]))

(defn submit-event-opts
  [{:keys [user-id item-id] :as edit}]
  {:original-qa-edit edit
   :on-success-main [::handlers/send-qa-edit-success
                     [::handlers/load-edit user-id item-id]]})

(defn submit-edit-button
  [item edit event-opts]
  (when (= :answer (:item-type item))
    [button
     {:appearance #{:primary :full-width}
      :disabled? @(rf/subscribe [::annotation-subs/loading?])
      :id :submit-annotation
      :on-click #(rf/dispatch [::annotation-handlers/submit (merge
                                                             {:item (assoc item :qa-edit-id (:id edit))}
                                                             (submit-event-opts edit)
                                                             event-opts)])}
     "Submit edit on behalf of annotator"]))

(defn assign-to-me-button
  [item user]
  (when (= :answer (:item-type item))
    [button
     {:appearance #{:primary :full-width}
      :on-click #(rf/dispatch [::handlers/assign-to-me
                               {:item item
                                :user user}])}
     "Assign to me"]))

(defn reject-button
  [item {:keys [report] :as edit} task-config]
  (when (and (= :answer (:item-type item))
             (not report)
             (task-config/modifier task-config :suppress-part-screen?))
    [button
     {:appearance #{:primary :full-width}
      :on-click #(rf/dispatch [::modal/show annotation-ids/reject-item-modal (submit-event-opts edit)])}
     "Reject as annotator"]))

(defn main-panel
  [title task-config item edit]
  {:pre [(not= :part (:item-type item))]}
  [:div.qa-view-container
   [annotation-components/alert-container]
   [panel {:appearance #{:simple}}
    [:h4 title]
    (if (and item edit)
      [:<>
       [layout {:appearance #{:qa-view-details}}
        [view-shared/item-details task-config item]
        (if-let [report (:report edit)]
          [report-details edit report]
          [view-shared/edit-details task-config item edit "Edit Details"])]
       [annotate-item item edit]
       [send-feedback item edit]
       [:div.action-buttons
        (if (view-shared/go-back-only?)
          [view-shared/go-back-button]
          (let [user @(rf/subscribe [::user-subs/user])]
            [:<>
             [button {:appearance #{:secondary :full-width}
                      :on-click #(rf/dispatch [:navigate-to [:manatee.edit.handlers/edits]])}
              "View all edits"]
             (when (and (= :answer (:item-type item))
                        (not (task-config/modifier task-config :suppress-part-screen?)))
               [button {:appearance #{:secondary :full-width}
                        :on-click #(rf/dispatch [:navigate-to [::handlers/item-group
                                                               {:user-id (:user-id edit)
                                                                :item-group-id (:item-group-id item)}]])}
                "View item group"])
             [submit-edit-button item edit {}]
             [reject-button item edit task-config]
             [approve-button item edit]
             [assign-to-me-button item user]]))]]
      [loading-spinner])]
   [annotation-components/display-raw-item-data task-config item]
   [annotation-components/reject-item-modal item]])

(defn edit-container
  [title]
  (let [edit @(rf/subscribe [::subs/edit])
        item @(rf/subscribe [::subs/item])
        task-config @(rf/subscribe [::task-config-subs/task-config (get-in item [:task-config-id])])]
    [main-panel title task-config item edit]))

(def edit-review-page
  {:template templates/single-column
   :main (partial edit-container "Review Completed Edit")})

(def reported-edit-review-page
  {:template templates/single-column
   :main (partial edit-container "Review Reported Edit")})
